import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import SubHeaders from '@/components/Headers/SubHeaders'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import AddIcons from '@/icons/AddIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import {
    getSLATemplates,
    deleteSLATemplate,
} from '@/modules/Service/services/sla'
import { SLATemplate } from '@/modules/Service/types/sla'
import { DataTableAction } from '@/components/ui/data-table-customized'
import { generatePathDatacentral, paths } from '@/utils/path'
import { getSLATemplateColumns } from './columns'

const SLATemplatesList = () => {
    const { t } = useTranslation('services')
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SLA_TEMPLATES],
    })

    const {
        data: templatesData,
        isFetching,
        renderState,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SLA_TEMPLATES],
        queryFn: () => getSLATemplates(),
    })

    const { mutate: deleteTemplate } = useMutationWithHandler(
        {
            mutationFn: (templateId: number) => deleteSLATemplate(templateId),
        },
        {
            successMessage: t('sla.template_deleted'),
            reactQueryKeys: QUERY_KEYS.SLA_TEMPLATES,
            onSuccess: () => refetch(),
        }
    )

    const handleCreate = () => {
        navigate(paths.slaTemplateCreate)
    }

    const handleEdit = (template: SLATemplate) => {
        navigate(
            generatePathDatacentral(paths.slaTemplateEdit, {
                id: template.sla_template_id,
            })
        )
    }

    const handleDelete = (template: SLATemplate) => {
        setConfirmation({
            open: true,
            title: t('sla.delete_template_title'),
            description: t('sla.delete_template_description'),
            onConfirm: () => deleteTemplate(template.sla_template_id),
        })
    }

    const columns = getSLATemplateColumns()

    const actions: DataTableAction<SLATemplate>[] = [
        {
            key: 'edit',
            label: t('common.edit', { defaultValue: 'Edit' }),
            icon: <EditIcons className="w-4 h-4" />,
            onClick: (row) => handleEdit(row),
        },
        {
            key: 'delete',
            label: t('common.delete', { defaultValue: 'Delete' }),
            icon: <DeleteOutlineIcons className="w-4 h-4" />,
            variant: 'danger',
            onClick: (row) => handleDelete(row),
        },
    ]

    const templates = templatesData?.results || []
    const stateComponent = renderState()

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.settings,
                        title: t('common.settings', {
                            defaultValue: 'Settings',
                        }),
                    },
                    {
                        path: paths.slaTemplates,
                        title: t('sla.templates_title'),
                    },
                ]}
                actions={[
                    {
                        icon: <AddIcons />,
                        label: t('sla.create_template'),
                        variant: 'contained',
                        onClick: handleCreate,
                    },
                ]}
            />

            <Paper>
                {stateComponent || (
                    <DataTableCustomized
                        columns={columns}
                        rows={templates}
                        rowKey={(row) => row.sla_template_id}
                        size="small"
                        resizable
                        isFetching={isFetching}
                        loading={!templates.length && isFetching}
                        emptyMessage={t('sla.no_templates', {
                            defaultValue: 'No SLA templates found',
                        })}
                        actions={actions}
                    />
                )}
            </Paper>
        </div>
    )
}

export default SLATemplatesList
